import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  CalendarRange,
  ChevronDownIcon,
  CopyPlus,
  EllipsisVertical,
  Pen,
  Plus,
  Star,
  StarOff,
  TableOfContents,
  Trash2,
} from "lucide-react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import InputDebounce from "../../components/InputDebounce";
import ConfirmDialog from "../../dialog/ConfirmDialog";
import FilterAction from "../../actions/FilterAction";
import { fromJS } from "immutable";
import TriangleAlertIcon from "../../icons/TriangleAlertIcon";
import OctagonAlertIcon from "../../icons/OctagonAlertIcon";
import LayoutAction from "../../actions/LayoutAction";
import ProjectAction from "../../actions/ProjectAction";
import { saveFavoriteTree } from "../../api/ProjectApi";
import TaskAction from "../../actions/TaskAction";
import PipelineSVG from "../../icons/svg/pipeline.svg?react";
import GridSVG from "../../icons/svg/grid.svg?react";

export const getIconByView = (view) => {
  let icon = (
    <div style={{ width: "16px", height: "16px" }}>
      <div className="w-3.5 h-3.5 flex items-center justify-center rounded-sm text-white bg-gray-300">
        <TableOfContents size={10} strokeWidth={2} />
      </div>
    </div>
  );
  if (view === "GRID") {
    icon = (
      <div style={{ width: "16px", height: "16px" }}>
        <GridSVG />
      </div>
    );
  } else if (view === "BOARD") {
    icon = (
      <div style={{ width: "16px", height: "16px" }}>
        <PipelineSVG />
      </div>
    );
  } else if (view === "CALENDAR") {
    icon = (
      <div style={{ width: "16px", height: "16px" }}>
        <div className="w-3.5 h-3.5 flex items-center justify-center rounded-sm text-white bg-cyan-500">
          <CalendarRange size={10} strokeWidth={2} />
        </div>
      </div>
    );
  }
  return icon;
};

interface ViewModeProps {
  projectId: string;
  contentType: string;
  projectView: string;
  projectViewId: string;
  appView: string;
}

const ViewMode = ({
  projectId,
  contentType,
  projectViewId,
  projectView,
  appView,
}: ViewModeProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const myFilters = useSelector((state: any) => state.filterReducer.get("myFilters")) || fromJS([]);
  const myFiltersByType = myFilters.filter((item) =>
    (item.getIn(["data", "types"]).toJS() || []).includes(contentType)
  );

  let myFilter =
    myFiltersByType.find(
      (item) => !item.get("id") && item.getIn(["data", "view"]) === projectView
    ) || fromJS({});
  if (projectViewId) {
    myFilter = myFiltersByType.find((item) => item.get("id") === projectViewId) || myFilter;
  }
  const favoriteTree =
    useSelector((state: any) => state.projectReducer.getIn(["project", "favoriteTree"])) ||
    fromJS([]);

  const [keywords, setKeywords] = useState("");
  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });

  const handleSubmitConfirm = () => {
    if (showConfirm.type === "REMOVE_VIEW") {
      dispatch(FilterAction.deleteFilter(showConfirm.data)).then(() => {
        dispatch(FilterAction.getMyFilters(projectId, contentType));
        dispatch(
          LayoutAction.setNotification({
            title: t("Remove view"),
            type: "success",
            message: t("Remove view successfully!"),
          })
        );
        if (projectViewId === showConfirm.data) {
          navigate(`/workspaces/${projectId}?view=GRID&type=${contentType}&key=${appView}`);
        }
      });
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  const handleClickViewMode = (id, view) => {
    if (id) {
      navigate(`/workspaces/${projectId}?id=${id}&view=${view}&type=${contentType}&key=${appView}`);
    } else {
      navigate(`/workspaces/${projectId}?view=${view}&type=${contentType}&key=${appView}`);
    }
  };

  const getAllFavoriteSavedFilters = (tree = []) => {
    let result = [];
    for (const node of tree) {
      if (node.nodeType === "SavedFilter") {
        result.push(node);
      }
      if (node.children?.length) {
        result = result.concat(getAllFavoriteSavedFilters(node.children));
      }
    }
    return result;
  };

  const removeFavoriteSavedFilterById = (tree = [], idToRemove) => {
    return tree
      .filter((node) => !(node.nodeType === "SavedFilter" && node.filterId === idToRemove))
      .map((node) => ({
        ...node,
        children: node.children?.length
          ? removeFavoriteSavedFilterById(node.children, idToRemove)
          : [],
      }));
  };

  const renderViews = (closeList) => {
    const favoriteFilters = getAllFavoriteSavedFilters(favoriteTree.toJS()).map(
      (item) => item.filterId
    );
    return myFiltersByType
      .toJS()
      .filter((item) =>
        keywords ? item.name.trim().toLowerCase().includes(keywords.trim().toLowerCase()) : true
      )
      .map((item, index) => {
        const isFavorite = favoriteFilters.includes(item.id);
        return (
          <div
            key={`${index}-${item.data.view}`}
            className="w-full flex items-center justify-between"
          >
            <button
              className="block w-full px-3 py-2 hover:bg-gray-100"
              onClick={() => {
                handleClickViewMode(item.id, item.data.view || "GRID");
                closeList();
              }}
            >
              <div className="flex items-center space-x-2">
                {getIconByView(item.data.view)}
                <div className="text-nowrap text-sm font-medium">{item.name}</div>
              </div>
            </button>
            {item.id ? (
              <Popover>
                <PopoverButton className="w-full flex items-center justify-between space-x-2 p-2 cursor-default hover:bg-gray-100 outline-none">
                  <EllipsisVertical className="w-4 h-4" />
                </PopoverButton>
                <PopoverPanel anchor="right start" className="[--anchor-gap:12px] z-20">
                  {({ close }) => (
                    <div className="w-max p-2 rounded-md bg-white border border-gray-300">
                      <button
                        className="block w-full px-3 py-2 flex items-center gap-2 text-sm font-medium hover:bg-gray-100"
                        onClick={async () => {
                          let updatedFavoriteTree;
                          if (isFavorite) {
                            updatedFavoriteTree = removeFavoriteSavedFilterById(
                              favoriteTree.toJS(),
                              item.id
                            );
                          } else {
                            updatedFavoriteTree = [
                              ...favoriteTree.toJS(),
                              {
                                id: `SavedFilter-${Date.now()}`,
                                name: item.name,
                                nodeType: "SavedFilter",
                                filterId: item.id,
                                children: [],
                              },
                            ];
                          }
                          await saveFavoriteTree(projectId, updatedFavoriteTree);
                          dispatch(ProjectAction.getFavoriteTree(projectId));
                          dispatch(
                            LayoutAction.setNotification({
                              title: t("Favorites"),
                              type: "success",
                              message: isFavorite
                                ? t("Remove from favorites successfully")
                                : t("Add to favorite successfully"),
                            })
                          );
                          close();
                        }}
                      >
                        {isFavorite ? (
                          <>
                            <StarOff className="w-4 h-4 text-gray-600" />
                            <span>{t("Remove from favorites")}</span>
                          </>
                        ) : (
                          <>
                            <Star className="w-4 h-4 text-gray-600" />
                            <span>{t("Add to favorite")}</span>
                          </>
                        )}
                      </button>
                      <button
                        className="block w-full px-3 py-2 flex items-center gap-2 text-sm font-medium hover:bg-gray-100"
                        onClick={() => {
                          dispatch(TaskAction.showViewModeDialog(item));
                          close();
                        }}
                      >
                        <Pen className="w-4 h-4 text-gray-600" />
                        <span>{t("Edit")}</span>
                      </button>
                      <button
                        className="block w-full px-3 py-2 flex items-center gap-2 text-sm font-medium hover:bg-gray-100"
                        onClick={() => {
                          const requestData = {
                            ...item,
                            name: `${item.name} (Copy)`,
                          };
                          delete requestData.id;
                          dispatch(FilterAction.saveFilter(requestData)).then(({ payload }) => {
                            dispatch(FilterAction.getMyFilters(projectId, contentType));
                            if (payload && payload.id) {
                              navigate(
                                `/workspaces/${projectId}?id=${payload.id}&view=${
                                  payload.data.view || "GRID"
                                }&type=${contentType}&key=${appView}`
                              );
                            }
                          });
                          close();
                        }}
                      >
                        <CopyPlus className="w-4 h-4 text-gray-600" />
                        <span>{t("Duplicate")}</span>
                      </button>
                      <div className="w-full my-1 border-b border-gray-200"></div>
                      <button
                        className="block w-full px-3 py-2 flex items-center gap-2 text-red-500 text-sm font-medium hover:bg-gray-100"
                        onClick={() => {
                          setShowConfirm({
                            show: true,
                            title: t("Remove view"),
                            description: t("Are you sure you want to remove this view?"),
                            alertIcon: <TriangleAlertIcon />,
                            dangerBtn: true,
                            type: "REMOVE_VIEW",
                            data: item.id,
                          });
                          close();
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                        <span>{t("Remove")}</span>
                      </button>
                    </div>
                  )}
                </PopoverPanel>
              </Popover>
            ) : null}
          </div>
        );
      });
  };

  return (
    <div>
      <Popover>
        <PopoverButton className="inline-flex items-center space-x-2 rounded-md h-8 px-2 py-1.5 text-sm font-medium text-gray-500 border border-gray-300 outline-none">
          {getIconByView(projectView)}
          <div className="mr-2">{myFilter.get("name")}</div>
          <ChevronDownIcon className="h-4 w-4 text-gray-500" />
        </PopoverButton>
        <PopoverPanel
          anchor="bottom start"
          className="z-20 mt-2 p-2 bg-white rounded-md border border-gray-300 shadow-xl outline-none"
        >
          {({ close }) => (
            <div className="w-full">
              <InputDebounce
                autoFocus
                className={`w-full h-8 mb-2 px-2 placeholder:text-gray-400 text-sm border-b border-gray-200 focus:outline-none`}
                placeholder={t("Search view...")}
                value={keywords}
                onChange={(value: string) => {
                  setKeywords(value);
                }}
              />
              <div className="w-full max-h-120 overflow-y-auto">{renderViews(close)}</div>
              <div
                className="w-full inline-flex items-center mt-2 pt-2 px-2 space-x-2 text-sm border-t border-gray-200 cursor-pointer"
                onClick={() => {
                  dispatch(
                    TaskAction.showViewModeDialog({
                      name: "",
                      visibility: "Private",
                      data: {},
                    })
                  );
                }}
              >
                <Plus className="w-4 h-4" />
                <div>{t("Save as new view")}</div>
              </div>
            </div>
          )}
        </PopoverPanel>
      </Popover>
      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
        dangerBtn={showConfirm.dangerBtn}
        onSubmit={handleSubmitConfirm}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </div>
  );
};

export default ViewMode;
